import classnames from 'classnames/bind';
import React from 'react';

import APIError from 'types/api_error';

import { useAPI } from 'hooks/api/useAPI';
import { isObject, objectHas } from 'services/object';

import { Text } from 'components/Text/Text';

import { BasePlayerProps, WebRTCPlayerMode } from './types';
import styles from './WebRTCPlayer.module.scss';

const c = classnames.bind(styles);

export function WebRTCPlayerLegacy({
  className,
  streamID,
  auto,
}: BasePlayerProps) {
  const api = useAPI();
  const [mode, setMode] = React.useState<WebRTCPlayerMode>('loading');
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [attempts, setAttempts] = React.useState(1);

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const connection = React.useRef<RTCPeerConnection>();
  const peerID = React.useRef<string>();
  const shouldUseFallbackProfileLevel = React.useRef(false);

  React.useEffect(() => {
    setMode('loading');

    function showErrorState(error: APIError | unknown) {
      setMode('error');

      if (
        error instanceof Error &&
        (error.name === 'OperationError' ||
          error.name === 'InvalidAccessError' ||
          error.name === 'RTCError')
      ) {
        shouldUseFallbackProfileLevel.current = true;
      }

      if (isObject(error) && objectHas(error, 'message')) {
        setErrorMessage(
          `${(error as { message: string }).message}. Retrying in 2 seconds..`
        );
      } else {
        setErrorMessage(`${String(error)}. Retrying in 2 seconds..`);
      }

      window.setTimeout(() => {
        setAttempts((prev) => prev + 1);
      }, 2000);
    }

    function onRemoteDescriptionSet() {
      if (!connection.current) {
        return;
      }

      const options: RTCOfferOptions = { offerToReceiveVideo: true };

      connection.current
        .createAnswer(options)
        .then(onAnswerGenerated)
        .catch(showErrorState);
    }

    function onAnswerGenerated(value: RTCSessionDescriptionInit) {
      if (!connection.current) {
        return;
      }

      connection.current
        .setLocalDescription(value)
        .then(() => onLocalDescriptionSet(value))
        .catch(showErrorState);
    }

    function onLocalDescriptionSet(event: RTCSessionDescriptionInit) {
      if (!peerID.current) {
        return;
      }

      const { type, sdp } = event;

      api.webrtc
        .setAnswer(streamID, peerID.current, { type, sdp })
        .catch(showErrorState);

      pollCandidates();
    }

    function pollCandidates() {
      if (!peerID.current) {
        return;
      }

      api.webrtc
        .getCandidates(streamID, peerID.current)
        .then((res) => {
          if (res) {
            res.candidates.forEach(setRemoteCandidate);
            if (!res.completed) {
              setTimeout(pollCandidates, 1500);
            }
          }
        })
        .catch(showErrorState);
    }

    function setRemoteCandidate(candidate: RTCIceCandidateInit) {
      if (!connection.current) {
        return;
      }

      connection.current.addIceCandidate(candidate).catch(showErrorState);
    }

    api.webrtc
      .getOffer(streamID)
      .then((res) => {
        if (res) {
          const config = { iceServers: res.iceServers };

          peerID.current = res.peer_id;

          connection.current = new RTCPeerConnection(config);

          connection.current.onicegatheringstatechange = () => {};

          connection.current.onicecandidate = ({ candidate }) => {
            if (candidate && peerID.current) {
              api.webrtc.addCandidate(streamID, peerID.current, candidate);
            }
          };

          connection.current.ontrack = ({ streams }) => {
            if (videoRef.current) {
              [videoRef.current.srcObject] = streams;
            }
          };

          if (shouldUseFallbackProfileLevel.current) {
            res.sdp = res.sdp.replace(
              /profile-level-id=(?!42)[0-9a-f]{6}/,
              'profile-level-id=42e01f'
            );
          }

          connection.current
            .setRemoteDescription(res)
            .then(onRemoteDescriptionSet)
            .catch(showErrorState);
        }
      })
      .catch(showErrorState);

    return () => {
      if (connection.current && peerID.current) {
        connection.current.onicegatheringstatechange = null;
        connection.current.onicecandidate = null;
        connection.current.ontrack = null;
        connection.current.oniceconnectionstatechange = null;
        connection.current.close();
        connection.current = undefined;
        api.webrtc.close(streamID, peerID.current).catch(showErrorState);
      }
    };
  }, [attempts, streamID, api.webrtc]);

  React.useEffect(() => {
    const loadedCheck = window.setInterval(() => {
      if (videoRef.current) {
        const video = videoRef.current;
        if (video.readyState >= 2) {
          setMode('idle');
        }
      }
    }, 1000);

    return () => {
      window.clearInterval(loadedCheck);
    };
  }, [attempts]);

  return (
    <div id={streamID} className={c('wrap', className, mode, { auto })}>
      {mode === 'loading' && (
        <div className={c('loader')}>
          <div className="loading-spinner" />
        </div>
      )}

      <video ref={videoRef} className={c('video')} muted controls autoPlay />

      {mode === 'error' && (
        <div className={c('error-message')}>
          <p>
            Stream could not be loaded due to network issues or incompatible
            format.
            {errorMessage && (
              <Text inline>
                <br />
                {errorMessage}
              </Text>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
